import { useEffect, useLayoutEffect, useState, type RefObject } from "react";
import type { WorkspaceControlPlacement } from "./WorkspaceControlGroup.js";

/* The width measurements of `WorkspaceControlGroup`, kept out of the component
 * so its file holds placement, paint and the collapse policy only (the
 * top-chrome inset is `workspace-top-chrome-inset.ts`). Internal: not exported
 * from the barrel. */

export interface WorkspaceControlMeasure {
  readonly container: number;
  readonly rail: number;
  readonly counterpart: number;
  readonly counterpartHeight: number;
  /**
   * The top-start occupant can still narrow itself: it, or something inside
   * it, carries `data-can-shrink="true"` (VSM's workshop pill steps down its
   * labels). A collision-folded group waits for it before dropping a row.
   */
  readonly counterpartCanShrink: boolean;
  readonly self: number;
}

/** The attribute a top-start occupant sets while it can still give way. */
export const CAN_SHRINK_ATTRIBUTE = "data-can-shrink";

function readViewportWidth(): number {
  if (typeof window === "undefined") return 0;
  return Math.max(window.visualViewport?.width ?? window.innerWidth, 0);
}

/** The visual viewport width, for true-phone stacking. */
export function useVisualViewportWidth(): number {
  const [viewportWidth, setViewportWidth] = useState(readViewportWidth);
  useEffect(() => {
    const report = (): void => setViewportWidth(readViewportWidth());
    const visualViewport = window.visualViewport;
    window.addEventListener("resize", report);
    visualViewport?.addEventListener("resize", report);
    return () => {
      window.removeEventListener("resize", report);
      visualViewport?.removeEventListener("resize", report);
    };
  }, []);
  return viewportWidth;
}

/**
 * The widths a group's density and collision fold read.
 *
 * Viewport breakpoints alone miss the important case for workspace chrome: a
 * right rail consumes part of the viewport while the viewport itself stays
 * wide. A corner group observes its containing block, so opening or resizing
 * a rail publishes the width the controls can actually use. A top-end group
 * also measures the top-start occupant (`[data-placement="top-start"]`) and
 * itself, feeding the collision fold.
 *
 * An inline group measures the closest `[data-entity-workspace]` instead: its
 * parent (the Canvas statusSlot box, a centring row) is sized to its content,
 * so the parent's width says nothing about the room the controls have. The
 * workspace already contains the rail slot, so only the settled rail width is
 * taken off. With no workspace it stays `null` and the group keeps the
 * media-query fallback.
 */
export function useWorkspaceControlMeasure(
  ref: RefObject<HTMLElement | null>,
  placement: WorkspaceControlPlacement,
  settledRailWidth: number,
): WorkspaceControlMeasure | null {
  const [measuredWidths, setMeasuredWidths] =
    useState<WorkspaceControlMeasure | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    const container = node?.parentElement;
    if (!node || !container) return;
    const workspace = container.closest<HTMLElement>("[data-entity-workspace]");
    const inline = placement === "inline";
    const measured = inline ? workspace : container;
    if (!measured) return;
    const railSlot = inline
      ? null
      : workspace?.querySelector<HTMLElement>(
          "[data-workspace-right-rail-slot]",
        );
    let observedCounterpart: HTMLElement | null = null;
    const findCounterpart = (): HTMLElement | null => {
      if (placement !== "top-end") return null;
      const candidate = (workspace ?? container).querySelector<HTMLElement>(
        '[data-placement="top-start"]',
      );
      return candidate === node ? null : candidate;
    };
    const measure = (): void => {
      const containerWidth = measured.getBoundingClientRect().width;
      const movingRailWidth = railSlot?.getBoundingClientRect().width ?? 0;
      // Re-query per measure: the counterpart can mount later or be swapped
      // (VSM replaces its context bar's contents per framing). Re-observing
      // keeps its width changes flowing into the collision fold.
      const counterpart = findCounterpart();
      if (observer && counterpart !== observedCounterpart) {
        if (observedCounterpart) observer.unobserve(observedCounterpart);
        if (counterpart) observer.observe(counterpart);
        observedCounterpart = counterpart;
      }
      const counterpartWidth = counterpart?.getBoundingClientRect().width ?? 0;
      const counterpartHeight =
        counterpart?.getBoundingClientRect().height ?? 0;
      const counterpartCanShrink =
        counterpart !== null &&
        (counterpart.getAttribute(CAN_SHRINK_ATTRIBUTE) === "true" ||
          counterpart.querySelector(`[${CAN_SHRINK_ATTRIBUTE}="true"]`) !==
            null);
      const selfWidth = node.getBoundingClientRect().width;
      // The container and slot widths move in opposite directions during the
      // push transition. Their sum is the stable workspace width; subtracting
      // the settled rail width gives controls their final available width for
      // the whole animation, so they do not cross breakpoints frame by frame.
      // jsdom and a hidden story can report zero. Keep the media-query fallback
      // in those environments instead of collapsing every control.
      if (containerWidth > 0) {
        setMeasuredWidths((current) =>
          current !== null &&
          Math.abs(current.container - containerWidth) < 0.5 &&
          Math.abs(current.rail - movingRailWidth) < 0.5 &&
          Math.abs(current.counterpart - counterpartWidth) < 0.5 &&
          Math.abs(current.counterpartHeight - counterpartHeight) < 0.5 &&
          current.counterpartCanShrink === counterpartCanShrink &&
          Math.abs(current.self - selfWidth) < 0.5
            ? current
            : {
                container: containerWidth,
                rail: movingRailWidth,
                counterpart: counterpartWidth,
                counterpartHeight,
                counterpartCanShrink,
                self: selfWidth,
              },
        );
      }
    };
    // Created before the first measure() so that call can already begin
    // observing the counterpart it finds.
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    const topologyObserver =
      placement !== "top-end" || typeof MutationObserver === "undefined"
        ? undefined
        : new MutationObserver(measure);
    // The can-shrink mark can flip without any size change (a level that
    // changes nothing in its bar), so its attribute is watched as well.
    topologyObserver?.observe(workspace ?? container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [CAN_SHRINK_ATTRIBUTE],
    });
    measure();
    if (observer === undefined) {
      window.addEventListener("resize", measure);
      return () => {
        topologyObserver?.disconnect();
        window.removeEventListener("resize", measure);
      };
    }
    observer.observe(measured);
    observer.observe(node);
    if (railSlot) observer.observe(railSlot);
    return () => {
      topologyObserver?.disconnect();
      observer.disconnect();
    };
  }, [placement, ref, settledRailWidth]);

  return measuredWidths;
}
